import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";

const DAYS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

const DEFAULT_HOURS = DAYS?.reduce((acc, d) => {
  acc[d.key] = { open: "06:00", close: "22:00", closed: d?.key === "sun" };
  return acc;
}, {});

export default function GymSetup() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const logoInputRef = useRef(null);

  const [form, setForm] = useState({
    gym_name: "",
    tagline: "",
    description: "",
    email: "",
    phone: "",
    website: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    country: "",
    postal_code: "",
    facebook_url: "",
    instagram_url: "",
    twitter_url: "",
    youtube_url: "",
  });
  const [workingHours, setWorkingHours] = useState(DEFAULT_HOURS);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoUrl, setLogoUrl] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [activeSection, setActiveSection] = useState("basic");
  const [existingSettingsId, setExistingSettingsId] = useState(null);

  // Load existing settings if any
  useEffect(() => {
    const loadSettings = async () => {
      if (!user?.id) return;
      try {
        const { data } = await api.get('/gym-settings');
        if (data) {
          setExistingSettingsId(data.id);
          setForm({
            gym_name: data.gym_name || "",
            tagline: data.tagline || "",
            description: data.description || "",
            email: data.email || "",
            phone: data.phone || "",
            website: data.website || "",
            address_line1: data.address_line1 || "",
            address_line2: data.address_line2 || "",
            city: data.city || "",
            state: data.state || "",
            country: data.country || "",
            postal_code: data.postal_code || "",
            facebook_url: data.facebook_url || "",
            instagram_url: data.instagram_url || "",
            twitter_url: data.twitter_url || "",
            youtube_url: data.youtube_url || "",
          });
          if (data.logo_url) {
            setLogoUrl(data.logo_url);
            setLogoPreview(data.logo_url);
          }
          if (data.working_hours) setWorkingHours(data.working_hours);
        }
      } catch (err) {
        console.error("Error loading gym settings:", err);
      }
    };
    loadSettings();
  }, [user?.id]);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors?.[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleLogoChange = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file?.type)) {
      setErrors((prev) => ({ ...prev, logo: "Only JPG, PNG, WebP, or GIF images are allowed" }));
      return;
    }
    if (file?.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, logo: "Logo must be under 5MB" }));
      return;
    }
    setErrors((prev) => ({ ...prev, logo: "" }));
    setLogoPreview(URL.createObjectURL(file));
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/upload/gym-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const uploadedUrl = data?.url || "";
      setLogoUrl(uploadedUrl);
      setLogoPreview(uploadedUrl || URL.createObjectURL(file));
      if (existingSettingsId && uploadedUrl) {
        await api.patch('/gym-settings', { logo_url: uploadedUrl });
      }
    } catch (err) {
      setErrors((prev) => ({ ...prev, logo: err?.response?.data?.message || "Logo upload failed. Please try again." }));
      setLogoPreview(null);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleHoursChange = (day, field, value) => {
    setWorkingHours((prev) => ({
      ...prev,
      [day]: { ...prev?.[day], [field]: value },
    }));
  };

  const sections = [
    { key: "basic", label: "Basic Info", icon: "Building2" },
    { key: "contact", label: "Contact & Address", icon: "MapPin" },
    { key: "hours", label: "Working Hours", icon: "Clock" },
    { key: "social", label: "Social Media", icon: "Share2" },
  ];

  const currentSectionIndex = sections.findIndex((s) => s?.key === activeSection);
  const isLastSection = currentSectionIndex === sections.length - 1;

  const validateCurrentTab = () => {
    const errs = {};
    if (activeSection === "basic") {
      if (!form?.gym_name?.trim()) errs.gym_name = "Gym name is required";
    }
    if (activeSection === "contact") {
      if (!form?.email?.trim()) errs.email = "Email is required";
      else if (!/\S+@\S+\.\S+/?.test(form?.email)) errs.email = "Enter a valid email";
      if (!form?.phone?.trim()) errs.phone = "Phone number is required";
    }
    return errs;
  };

  const handleSave = async (andAdvance = false) => {
    const errs = validateCurrentTab();
    if (Object.keys(errs)?.length > 0) {
      setErrors(errs);
      return;
    }

    setSaving(true);
    try {
      const markComplete = andAdvance && isLastSection;
      const payload = {
        ...form,
        logo_url: logoUrl || null,
        working_hours: workingHours,
        setup_completed: markComplete,
      };

      if (existingSettingsId) {
        await api.patch('/gym-settings', payload);
      } else {
        const { data } = await api.post('/gym-settings', payload);
        setExistingSettingsId(data?.id);
      }

      if (andAdvance) {
        if (isLastSection) {
          navigate("/gym-owner-portal", { replace: true });
        } else {
          setErrors({});
          setActiveSection(sections[currentSectionIndex + 1]?.key);
        }
      } else {
        setErrors({ _success: "Settings saved successfully!" });
        setTimeout(() => setErrors((prev) => ({ ...prev, _success: "" })), 3000);
      }
    } catch (err) {
      console.error("Save error:", err);
      setErrors((prev) => ({ ...prev, _error: err?.response?.data?.message || err?.message || "Failed to save settings. Please try again." }));
    } finally {
      setSaving(false);
    }
  };

  const inputClass = (name) =>
    `w-full h-10 px-3 rounded-lg bg-[var(--color-input)] border text-sm font-[var(--font-body)] text-[var(--color-foreground)] placeholder-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] transition-all ${
      errors?.[name]
        ? "border-[var(--color-error)]"
        : "border-[var(--color-border)] focus:border-[var(--color-primary)]"
    }`;

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      {/* Header */}
      <div className="bg-[var(--color-card)] border-b border-[var(--color-border)] px-4 md:px-8 py-4 sticky top-0 z-10 shadow-[var(--shadow-sm)]">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[var(--color-primary)] flex items-center justify-center">
              <Icon name="Zap" size={18} color="white" strokeWidth={2.5} />
            </div>
            <div>
              <h1 className="text-base font-bold font-[var(--font-heading)] text-[var(--color-foreground)]">
                Fit<span className="text-[var(--color-primary)]">Pulse</span>
              </h1>
              <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Gym Setup</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleSave(false)}
              disabled={saving || uploadingLogo}
              className="px-4 py-2 text-sm font-medium font-[var(--font-caption)] border border-[var(--color-border)] text-[var(--color-muted-foreground)] rounded-lg hover:text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors disabled:opacity-50"
            >
              Save Draft
            </button>
            <button
              onClick={() => handleSave(true)}
              disabled={saving || uploadingLogo}
              className="flex items-center gap-2 px-5 py-2 text-sm font-semibold font-[var(--font-caption)] bg-[var(--color-primary)] text-white rounded-lg hover:bg-[var(--color-secondary)] transition-colors disabled:opacity-60 shadow-[var(--shadow-sm)]"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </>
              ) : isLastSection ? (
                <>
                  <Icon name="CheckCircle" size={15} color="white" />
                  Complete Setup
                </>
              ) : (
                <>
                  <Icon name="ArrowRight" size={15} color="white" />
                  Save & Continue
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        {/* Welcome Banner */}
        <div className="bg-gradient-to-r from-[var(--color-primary)]/10 to-[var(--color-secondary)]/5 border border-[var(--color-primary)]/20 rounded-xl p-5 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-[var(--color-primary)]/15 flex items-center justify-center flex-shrink-0">
              <Icon name="Sparkles" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-base font-bold font-[var(--font-heading)] text-[var(--color-foreground)] mb-1">
                Welcome! Let's set up your gym profile
              </h2>
              <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-body)]">
                Complete your gym details below. This information will be visible to your members and staff. You can always update it later from the Settings tab.
              </p>
            </div>
          </div>
        </div>

        {/* Global messages */}
        {errors?._success && (
          <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-[var(--color-success)]/10 border border-[var(--color-success)]/20">
            <Icon name="CheckCircle" size={16} color="var(--color-success)" />
            <p className="text-sm text-[var(--color-success)] font-[var(--font-caption)]">{errors?._success}</p>
          </div>
        )}
        {errors?._error && (
          <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-[var(--color-error)]/10 border border-[var(--color-error)]/20">
            <Icon name="AlertCircle" size={16} color="var(--color-error)" />
            <p className="text-sm text-[var(--color-error)] font-[var(--font-caption)]">{errors?._error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl p-3 sticky top-24">
              <p className="text-xs font-semibold font-[var(--font-caption)] text-[var(--color-muted-foreground)] uppercase tracking-wider px-2 mb-2">
                Sections
              </p>
              <nav className="space-y-1">
                {sections?.map((s) => (
                  <button
                    key={s?.key}
                    onClick={() => setActiveSection(s?.key)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium font-[var(--font-caption)] transition-colors text-left ${
                      activeSection === s?.key
                        ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                        : "text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] hover:bg-[var(--color-muted)]"
                    }`}
                  >
                    <Icon
                      name={s?.icon}
                      size={15}
                      color={activeSection === s?.key ? "var(--color-primary)" : "currentColor"}
                    />
                    {s?.label}
                  </button>
                ))}
              </nav>

              {/* Logo Upload in sidebar */}
              <div className="mt-4 pt-4 border-t border-[var(--color-border)]">
                <p className="text-xs font-semibold font-[var(--font-caption)] text-[var(--color-muted-foreground)] uppercase tracking-wider px-2 mb-3">
                  Gym Logo
                </p>
                <div className="flex flex-col items-center gap-3">
                  <div
                    onClick={() => !uploadingLogo && logoInputRef?.current?.click()}
                    className="relative w-20 h-20 rounded-xl border-2 border-dashed border-[var(--color-border)] flex items-center justify-center cursor-pointer hover:border-[var(--color-primary)] hover:bg-[var(--color-primary)]/5 transition-all overflow-hidden"
                  >
                    {logoPreview ? (
                      <img src={logoPreview} alt="Gym logo preview" className="w-full h-full object-cover rounded-xl" />
                    ) : (
                      <div className="flex flex-col items-center gap-1">
                        <Icon name="ImagePlus" size={20} color="var(--color-muted-foreground)" />
                        <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Upload</span>
                      </div>
                    )}
                    {uploadingLogo && (
                      <div className="absolute inset-0 rounded-xl bg-black/40 flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                  </div>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleLogoChange}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => logoInputRef?.current?.click()}
                    className="text-xs font-medium font-[var(--font-caption)] text-[var(--color-primary)] hover:text-[var(--color-secondary)] transition-colors"
                  >
                    {logoPreview ? "Change Logo" : "Upload Logo"}
                  </button>
                  {errors?.logo && (
                    <p className="text-xs text-[var(--color-error)] font-[var(--font-caption)] text-center">{errors?.logo}</p>
                  )}
                  <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] text-center">
                    JPG, PNG, WebP up to 5MB
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Basic Info */}
            {activeSection === "basic" && (
              <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl p-6">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-7 h-7 rounded-lg bg-[var(--color-primary)]/10 flex items-center justify-center">
                    <Icon name="Building2" size={15} color="var(--color-primary)" />
                  </div>
                  <h3 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">
                    Basic Information
                  </h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1.5">
                      Gym Name <span className="text-[var(--color-error)]">*</span>
                    </label>
                    <input
                      type="text"
                      name="gym_name"
                      value={form?.gym_name}
                      onChange={handleChange}
                      placeholder="e.g. Iron Paradise Fitness"
                      className={inputClass("gym_name")}
                    />
                    {errors?.gym_name && (
                      <p className="mt-1 text-xs text-[var(--color-error)] font-[var(--font-caption)]">{errors?.gym_name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1.5">
                      Tagline
                    </label>
                    <input
                      type="text"
                      name="tagline"
                      value={form?.tagline}
                      onChange={handleChange}
                      placeholder="e.g. Train Hard. Live Strong."
                      className={inputClass("tagline")}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1.5">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={form?.description}
                      onChange={handleChange}
                      placeholder="Tell members about your gym, facilities, and what makes you unique..."
                      rows={4}
                      className="w-full px-3 py-2.5 rounded-lg bg-[var(--color-input)] border border-[var(--color-border)] text-sm font-[var(--font-body)] text-[var(--color-foreground)] placeholder-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] focus:border-[var(--color-primary)] transition-all resize-none"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Contact & Address */}
            {activeSection === "contact" && (
              <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl p-6">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-7 h-7 rounded-lg bg-[var(--color-primary)]/10 flex items-center justify-center">
                    <Icon name="MapPin" size={15} color="var(--color-primary)" />
                  </div>
                  <h3 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">
                    Contact & Address
                  </h3>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1.5">
                        Email Address <span className="text-[var(--color-error)]">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2">
                          <Icon name="Mail" size={14} color="var(--color-muted-foreground)" />
                        </div>
                        <input
                          type="email"
                          name="email"
                          value={form?.email}
                          onChange={handleChange}
                          placeholder="gym@example.com"
                          className={`${inputClass("email")} pl-9`}
                        />
                      </div>
                      {errors?.email && (
                        <p className="mt-1 text-xs text-[var(--color-error)] font-[var(--font-caption)]">{errors?.email}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1.5">
                        Phone Number <span className="text-[var(--color-error)]">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2">
                          <Icon name="Phone" size={14} color="var(--color-muted-foreground)" />
                        </div>
                        <input
                          type="tel"
                          name="phone"
                          value={form?.phone}
                          onChange={handleChange}
                          placeholder="+1 (555) 000-0000"
                          className={`${inputClass("phone")} pl-9`}
                        />
                      </div>
                      {errors?.phone && (
                        <p className="mt-1 text-xs text-[var(--color-error)] font-[var(--font-caption)]">{errors?.phone}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1.5">
                      Website
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2">
                        <Icon name="Globe" size={14} color="var(--color-muted-foreground)" />
                      </div>
                      <input
                        type="url"
                        name="website"
                        value={form?.website}
                        onChange={handleChange}
                        placeholder="https://www.yourgym.com"
                        className={`${inputClass("website")} pl-9`}
                      />
                    </div>
                  </div>

                  <div className="pt-2">
                    <p className="text-xs font-semibold font-[var(--font-caption)] text-[var(--color-muted-foreground)] uppercase tracking-wider mb-3">
                      Address
                    </p>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1.5">
                          Address Line 1
                        </label>
                        <input
                          type="text"
                          name="address_line1"
                          value={form?.address_line1}
                          onChange={handleChange}
                          placeholder="Street address, building number"
                          className={inputClass("address_line1")}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1.5">
                          Address Line 2
                        </label>
                        <input
                          type="text"
                          name="address_line2"
                          value={form?.address_line2}
                          onChange={handleChange}
                          placeholder="Suite, floor, unit (optional)"
                          className={inputClass("address_line2")}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1.5">
                            City
                          </label>
                          <input
                            type="text"
                            name="city"
                            value={form?.city}
                            onChange={handleChange}
                            placeholder="City"
                            className={inputClass("city")}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1.5">
                            State / Province
                          </label>
                          <input
                            type="text"
                            name="state"
                            value={form?.state}
                            onChange={handleChange}
                            placeholder="State"
                            className={inputClass("state")}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1.5">
                            Country
                          </label>
                          <input
                            type="text"
                            name="country"
                            value={form?.country}
                            onChange={handleChange}
                            placeholder="Country"
                            className={inputClass("country")}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1.5">
                            Postal Code
                          </label>
                          <input
                            type="text"
                            name="postal_code"
                            value={form?.postal_code}
                            onChange={handleChange}
                            placeholder="ZIP / Postal code"
                            className={inputClass("postal_code")}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Working Hours */}
            {activeSection === "hours" && (
              <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl p-6">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-7 h-7 rounded-lg bg-[var(--color-primary)]/10 flex items-center justify-center">
                    <Icon name="Clock" size={15} color="var(--color-primary)" />
                  </div>
                  <h3 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">
                    Working Hours
                  </h3>
                </div>
                <div className="space-y-3">
                  {DAYS?.map((day) => (
                    <div
                      key={day?.key}
                      className="flex items-center gap-3 p-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/20"
                    >
                      <div className="w-24 flex-shrink-0">
                        <span className="text-sm font-medium font-[var(--font-caption)] text-[var(--color-foreground)]">
                          {day?.label}
                        </span>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer flex-shrink-0">
                        <input
                          type="checkbox"
                          checked={!workingHours?.[day?.key]?.closed}
                          onChange={(e) => handleHoursChange(day?.key, "closed", !e?.target?.checked)}
                          className="rounded border-[var(--color-border)] text-[var(--color-primary)] focus:ring-[var(--color-ring)]"
                        />
                        <span className="text-xs font-[var(--font-caption)] text-[var(--color-muted-foreground)]">Open</span>
                      </label>
                      {!workingHours?.[day?.key]?.closed ? (
                        <div className="flex items-center gap-2 flex-1">
                          <input
                            type="time"
                            value={workingHours?.[day?.key]?.open || "06:00"}
                            onChange={(e) => handleHoursChange(day?.key, "open", e?.target?.value)}
                            className="flex-1 h-8 px-2 rounded-md bg-[var(--color-input)] border border-[var(--color-border)] text-sm font-[var(--font-body)] text-[var(--color-foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--color-ring)]"
                          />
                          <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">to</span>
                          <input
                            type="time"
                            value={workingHours?.[day?.key]?.close || "22:00"}
                            onChange={(e) => handleHoursChange(day?.key, "close", e?.target?.value)}
                            className="flex-1 h-8 px-2 rounded-md bg-[var(--color-input)] border border-[var(--color-border)] text-sm font-[var(--font-body)] text-[var(--color-foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--color-ring)]"
                          />
                        </div>
                      ) : (
                        <span className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] italic">
                          Closed
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Social Media */}
            {activeSection === "social" && (
              <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl p-6">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-7 h-7 rounded-lg bg-[var(--color-primary)]/10 flex items-center justify-center">
                    <Icon name="Share2" size={15} color="var(--color-primary)" />
                  </div>
                  <h3 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">
                    Social Media Links
                  </h3>
                </div>
                <div className="space-y-4">
                  {[
                    { name: "facebook_url", label: "Facebook", icon: "Facebook", placeholder: "https://facebook.com/yourgym" },
                    { name: "instagram_url", label: "Instagram", icon: "Instagram", placeholder: "https://instagram.com/yourgym" },
                    { name: "twitter_url", label: "Twitter / X", icon: "Twitter", placeholder: "https://twitter.com/yourgym" },
                    { name: "youtube_url", label: "YouTube", icon: "Youtube", placeholder: "https://youtube.com/@yourgym" },
                  ]?.map((social) => (
                    <div key={social?.name}>
                      <label className="block text-sm font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1.5">
                        {social?.label}
                      </label>
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2">
                          <Icon name={social?.icon} size={14} color="var(--color-muted-foreground)" />
                        </div>
                        <input
                          type="url"
                          name={social?.name}
                          value={form?.[social?.name]}
                          onChange={handleChange}
                          placeholder={social?.placeholder}
                          className={`${inputClass(social?.name)} pl-9`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bottom CTA */}
            <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl p-5">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">
                    Ready to get started?
                  </p>
                  <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">
                    Save your gym details and proceed to create your first branch.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleSave(false)}
                    disabled={saving || uploadingLogo}
                    className="px-4 py-2 text-sm font-medium font-[var(--font-caption)] border border-[var(--color-border)] text-[var(--color-muted-foreground)] rounded-lg hover:text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors disabled:opacity-50"
                  >
                    Save Draft
                  </button>
                  <button
                    onClick={() => handleSave(true)}
                    disabled={saving || uploadingLogo}
                    className="flex items-center gap-2 px-6 py-2 text-sm font-semibold font-[var(--font-caption)] bg-[var(--color-primary)] text-white rounded-lg hover:bg-[var(--color-secondary)] transition-colors disabled:opacity-60 shadow-[var(--shadow-sm)]"
                  >
                    {saving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Saving...
                      </>
                    ) : isLastSection ? (
                      <>
                        <Icon name="CheckCircle" size={15} color="white" />
                        Complete Setup
                      </>
                    ) : (
                      <>
                        <Icon name="ArrowRight" size={15} color="white" />
                        Save & Continue
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
